import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader, ConfirmDialog } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem, SyutSelect, MinimizedModalStrip } from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from '../../../../services/notificationService';
import {
  timeEntryApi, TimeEntryDto,
  clientApi, ClientDto,
  projectApi, ProjectDto,
  type MyTimesheetQuery,
} from 'api/services/timeTrackerApi';
import TaskDetailScreen from './TaskDetailScreen';
import AdhocLogModal from './AdhocLogModal';
import { formatHM } from './duration';

const firstOfMonthIso = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const todayIso = () => new Date().toISOString().slice(0, 10);

type Kind = '' | 'task' | 'adhoc';
type GridRow = TimeEntryDto & { id: string };

interface MyTimesheetScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const MyTimesheetScreen: React.FC<MyTimesheetScreenProps> = ({ tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  // Server-side pagination + filter state
  const [page, setPage]         = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');

  const [dateFrom, setDateFrom] = useState<string>(firstOfMonthIso());
  const [dateTo, setDateTo]     = useState<string>(todayIso());
  const [clientId, setClientId] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('');
  const [kind, setKind]         = useState<Kind>('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Data
  const [rows, setRows]           = useState<TimeEntryDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading]     = useState(false);

  // Lookups
  const [clients, setClients]   = useState<ClientDto[]>([]);
  const [projects, setProjects] = useState<ProjectDto[]>([]);

  // Modals
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [editingAdhoc, setEditingAdhoc]     = useState<TimeEntryDto | null>(null);
  const [quickLogOpen, setQuickLogOpen]     = useState(false);
  const detailCloseGuardRef = useRef<(() => void) | null>(null);
  const quickLogMinimizeRef = useRef<(() => void) | null>(null);
  const editAdhocMinimizeRef = useRef<(() => void) | null>(null);

  // Delete
  const [pendingDelete, setPendingDelete] = useState<TimeEntryDto | null>(null);
  const [deleting, setDeleting]           = useState(false);

  // Lookups on mount
  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          clientApi.list({ pageSize: 200 }),
          projectApi.list({ pageSize: 200 }),
        ]);
        setClients(c.items);
        setProjects(p.items);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q: MyTimesheetQuery = {
        page: page + 1,
        pageSize,
        searchTerm: searchQuery || undefined,
        dateFrom: dateFrom || undefined,
        dateTo:   dateTo   || undefined,
        clientId: clientId || undefined,
        projectId: projectId || undefined,
        kind: (kind || undefined) as MyTimesheetQuery['kind'],
      };
      const res = await timeEntryApi.listMine(q);
      setRows(res.items);
      setTotalCount(res.totalCount);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err.message || 'Failed to load timesheet.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, dateFrom, dateTo, clientId, projectId, kind]);

  // Debounced auto-load on any filter change
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
  }, [load]);

  // Summary total for currently loaded page + total count
  const pageTotalHours = useMemo(
    () => rows.reduce((sum, r) => sum + Number(r.hours ?? 0), 0),
    [rows]
  );

  // Column definitions
  const columns: SyutGridColumn<GridRow>[] = useMemo(() => [
    {
      id: 'entryDate', header: 'Date', accessorKey: 'entryDate', size: 110,
      cell: ({ getValue }) => (getValue() as string)?.slice(0, 10) ?? '—',
    },
    { id: 'clientName', header: 'Client', accessorKey: 'clientName', size: 160,
      cell: ({ getValue }) => (getValue() as string) || '—' },
    { id: 'projectName', header: 'Project', accessorKey: 'projectName', size: 160,
      cell: ({ getValue }) => (getValue() as string) || '—' },
    {
      id: 'taskTitle', header: 'Task', size: 240,
      cell: ({ row }) => {
        const r = row.original;
        if (r.taskId) {
          return (
            <button
              onClick={(e) => { e.stopPropagation(); setSelectedTaskId(r.taskId!); }}
              style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600, padding: 0, textAlign: 'left' }}
              title="Open task"
            >
              {r.taskTitle || 'Untitled task'}
            </button>
          );
        }
        return (
          <span className="badge badge-inactive" style={{ fontSize: '0.7rem' }}>Ad-hoc</span>
        );
      },
    },
    { id: 'hours', header: 'Duration', accessorKey: 'hours', size: 110,
      cell: ({ getValue }) => formatHM(Number(getValue() ?? 0)) },
    {
      id: 'narration', header: 'Narration / Notes', size: 320, enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        return (r.narration || r.notes || '—') as React.ReactNode;
      },
    },
  ], []);

  const openAdhocEdit = useCallback((row: GridRow) => {
    const modalId = tabId ? `${tabId}-form-modal-adhoc-edit-${row.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setEditingAdhoc(row);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const openQuickLog = useCallback(() => {
    const modalId = tabId ? `${tabId}-form-modal-adhoc-create-1` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setQuickLogOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const rowActions = useCallback((row: GridRow): ActionMenuItem[] => {
    const isAdhoc = !row.taskId;
    return [
      isAdhoc
        ? { id: 'edit', label: 'Edit',       icon: <IconSystem.edit size={16} />, onClick: () => openAdhocEdit(row) }
        : { id: 'open', label: 'Open task',  icon: <IconSystem.eye  size={16} />, onClick: () => setSelectedTaskId(row.taskId!) },
      { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => setPendingDelete(row), variant: 'danger' },
    ];
  }, [openAdhocEdit]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await timeEntryApi.remove(pendingDelete.id, pendingDelete.rowVersion);
      setPendingDelete(null);
      await load();
      notificationService.toast.success('Time entry deleted.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const clearFilters = useCallback(() => {
    setDateFrom(firstOfMonthIso());
    setDateTo(todayIso());
    setClientId('');
    setProjectId('');
    setKind('');
    setPage(0);
  }, []);

  const anyFilterActive = !!(clientId || projectId || kind);

  const clientOptions = useMemo(
    () => [{ value: '', label: 'All clients' }, ...clients.map(c => ({ value: c.id, label: c.name }))],
    [clients]
  );
  const projectOptions = useMemo(
    () => [{ value: '', label: 'All projects' }, ...projects.map(p => ({ value: p.id, label: p.name }))],
    [projects]
  );
  const kindOptions = useMemo(
    () => [
      { value: '',      label: 'All entries' },
      { value: 'task',  label: 'Task-bound only' },
      { value: 'adhoc', label: 'Ad-hoc only' },
    ],
    []
  );

  const actions: ActionButton[] = useMemo(() => [
    { id: 'quick-log', icon: <IconSystem.add     size={18} />, label: 'Log Time', onClick: openQuickLog, tooltip: 'Log time without a task', variant: 'icon' },
    { id: 'refresh',   icon: <IconSystem.refresh size={18} />, label: 'Refresh',  onClick: load,                          tooltip: 'Refresh',                variant: 'icon' },
  ], [load, openQuickLog]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="My Timesheet"
        description={`Every entry you've logged — task-bound and ad-hoc. ${totalCount} entries · this page: ${formatHM(pageTotalHours)}.`}
        icon={<IconSystem.clock size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search narration, notes, task…' }}
        filterConfig={{ onClick: () => setFilterOpen(o => !o), isActive: filterOpen || anyFilterActive }}
        actions={actions}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <div className="data-grid-container" style={{ height: '100%' }}>
              <SyutGrid
                data={rows as GridRow[]}
                columns={columns}
                enablePagination
                manualPagination
                totalCount={totalCount}
                currentPage={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
                rowActions={rowActions}
                emptyMessage={loading ? 'Loading…' : 'No entries in this range.'}
              />
            </div>
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button onClick={() => setFilterOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', color: 'var(--text-muted)' }}>
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Date from</label>
              <input
                type="date"
                className="form-input"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Date to</label>
              <input
                type="date"
                className="form-input"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Client"
                options={clientOptions}
                value={clientId}
                onChange={(val) => { setClientId((val as string) || ''); setPage(0); }}
                placeholder="All clients"
                isClearable
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Project"
                options={projectOptions}
                value={projectId}
                onChange={(val) => { setProjectId((val as string) || ''); setPage(0); }}
                placeholder="All projects"
                isClearable
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Kind"
                options={kindOptions}
                value={kind}
                onChange={(val) => { setKind(((val as string) || '') as Kind); setPage(0); }}
                placeholder="All entries"
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Done</button>
          </div>
        </div>
        )}
      </div>

      {/* Task detail modal (for task-bound entries) */}
      {selectedTaskId && (
        <div className="modal-backdrop" onClick={() => detailCloseGuardRef.current?.()}>
          <div
            className="modal-container"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 1100, width: '95%', height: '90vh', maxHeight: '90vh' }}
          >
            <TaskDetailScreen
              id={selectedTaskId}
              onBack={() => { setSelectedTaskId(null); load(); }}
              modalMode
              closeGuardRef={detailCloseGuardRef}
            />
          </div>
        </div>
      )}

      {/* Quick Log modal — create new ad-hoc entry */}
      <AdhocLogModal
        open={quickLogOpen}
        mode="create"
        onClose={() => setQuickLogOpen(false)}
        onSaved={load}
        clients={clients}
        projects={projects}
        tabId={tabId}
        moduleName={moduleName}
        minimizeRef={quickLogMinimizeRef}
        onRestoreModal={(m) => {
          if (m === 'create') setQuickLogOpen(true);
        }}
      />

      {/* Ad-hoc edit modal */}
      <AdhocLogModal
        open={editingAdhoc !== null}
        mode="edit"
        editing={editingAdhoc}
        onClose={() => setEditingAdhoc(null)}
        onSaved={() => { setEditingAdhoc(null); load(); }}
        clients={clients}
        projects={projects}
        tabId={tabId}
        moduleName={moduleName}
        minimizeRef={editAdhocMinimizeRef}
        onRestoreModal={(m, restored) => {
          if (m === 'edit' && restored) setEditingAdhoc(restored);
        }}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete Time Entry"
        message={pendingDelete
          ? `Delete the ${formatHM(pendingDelete.hours)} entry from ${pendingDelete.entryDate?.slice(0, 10)}?`
          : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleting}
        loadingMessage="Deleting…"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default MyTimesheetScreen;
